/**
 * TemplatePreview - PDF preview component for Template Builder
 *
 * Renders HTML template as PDF with mock data
 * Phase 4 of Template Builder
 */

import { Stack, Alert, Group, Button, Loader, Paper, Text, Badge } from "@mantine/core";
import {
  Document,
  Page,
  Text as PDFText,
  View,
  Image as PDFImage,
  PDFViewer,
  PDFDownloadLink,
  StyleSheet
} from "@react-pdf/renderer";
import { IconAlertCircle, IconDownload, IconRefresh, IconDatabase, IconTestPipe } from "@tabler/icons-react";
import { useState, useMemo, useCallback } from "react";
import { usePreviewResultados } from "../../hooks/usePreviewResultados";
import { useQuestionarioGroups } from "../../hooks/useQuestionarioGroups";
import type { TemplateImage } from "../../types/template";
import { htmlToReactPDF, type PageContent } from "../../utils/html-to-reactpdf";
import { generateMockReportData, formatDate, type MockReportData } from "../../utils/mock-report-data";
import { replaceVariables } from "../../utils/variable-replacer";

export interface TemplatePreviewProperties {
  /** HTML template to preview */
  htmlTemplate: string;
  /** Questionário ID for loading real grupos (optional) */
  questionarioId?: string;
  /** Template name for PDF filename */
  templateName?: string;
  /** Show download button */
  showDownload?: boolean;
  /** Template images for variable replacement */
  templateImages?: TemplateImage[];
}

/**
 * A4 page dimensions in points
 */
const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;

/**
 * PDF Styles
 */
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
    lineHeight: 1.5
  },
  fullPage: {
    padding: 0,
    fontSize: 12,
    fontFamily: "Helvetica",
    lineHeight: 1.5
  },
  fullPageBackground: {
    width: A4_WIDTH,
    height: A4_HEIGHT
  },
  fullPageContent: {
    marginTop: -A4_HEIGHT,
    width: A4_WIDTH,
    height: A4_HEIGHT,
    padding: 30
  },
  title: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: "bold"
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 8,
    fontWeight: "bold"
  },
  text: {
    fontSize: 12,
    marginBottom: 6
  },
  errorText: {
    color: "red",
    fontSize: 10,
    marginTop: 5
  }
});

/**
 * PDF Document Component
 */
interface PDFDocumentProperties {
  htmlContent: string;
  mockData: MockReportData;
  templateImages?: TemplateImage[];
}

/**
 * Render a single page with optional fullpage background
 * @param pageContent
 * @param index
 * @param allErrors
 */
function renderPage(pageContent: PageContent, index: number, allErrors: string[]) {
  if (pageContent.isFullPage && pageContent.backgroundImage) {
    // Fullpage with background image
    // Image first, then content with negative margin to overlap
    return (
      <Page key={index} size="A4" style={styles.fullPage} wrap={false}>
        <PDFImage src={pageContent.backgroundImage} style={styles.fullPageBackground} />
        <View style={styles.fullPageContent}>{pageContent.component}</View>
      </Page>
    );
  }

  if (pageContent.isFullPage) {
    // Fullpage without background (just content)
    return (
      <Page key={index} size="A4" style={styles.page} wrap={false}>
        {pageContent.component}
      </Page>
    );
  }

  // Regular page with padding
  return (
    <Page key={index} size="A4" style={styles.page}>
      {index === 0 && allErrors.length > 0 && (
        <View>
          <PDFText style={styles.errorText}>Erros de conversão:</PDFText>
          {allErrors.map((error, errorIndex) => (
            <PDFText key={errorIndex} style={styles.errorText}>
              • {error}
            </PDFText>
          ))}
        </View>
      )}
      {pageContent.component}
    </Page>
  );
}

/**
 *
 * @param root0
 * @param root0.htmlContent
 * @param root0.mockData
 * @param root0.templateImages
 */
function PDFDocument({ htmlContent, mockData, templateImages = [] }: PDFDocumentProperties) {
  // Replace variables in HTML
  const { processedHTML, errors: replacementErrors } = useMemo(() => {
    try {
      // Cast mockData to ReportData format for variable replacement
      const reportData = {
        ...mockData,
        questionarioId: "mock-questionario",
        empresaId: "mock-empresa",
        projetoId: "mock-projeto",
        empresaProjetoId: "mock-empresa-projeto",
        questionario: { ...mockData.questionario, id: "mock-questionario" },
        projeto: mockData.projeto,
        empresa: mockData.empresa
      } as any;

      const result = replaceVariables(htmlContent, reportData, { images: templateImages });

      return { processedHTML: result.html, errors: result.missingVariables };
    } catch (error) {
      console.error("Error replacing variables:", error);
      return { processedHTML: htmlContent, errors: [] };
    }
  }, [htmlContent, mockData, templateImages]);

  // Convert HTML to React-PDF components
  const {
    component,
    errors: conversionErrors,
    pages
  } = useMemo(() => {
    // Cast mockData to ReportData format for component rendering (radar, table)
    const reportData = {
      ...mockData,
      questionarioId: "mock-questionario",
      empresaId: "mock-empresa",
      projetoId: "mock-projeto",
      empresaProjetoId: "mock-empresa-projeto",
      questionario: { ...mockData.questionario, id: "mock-questionario" },
      projeto: mockData.projeto,
      empresa: mockData.empresa
    } as any;

    return htmlToReactPDF(processedHTML, { reportData });
  }, [processedHTML, mockData]);

  const allErrors = [...replacementErrors, ...conversionErrors];

  // If we have multiple pages (fullpage mode), render them
  if (pages && pages.length > 0) {
    return <Document>{pages.map((page, index) => renderPage(page, index, allErrors))}</Document>;
  }

  // Legacy single-page rendering
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {allErrors.length > 0 && (
          <View>
            <PDFText style={styles.errorText}>Erros de conversão:</PDFText>
            {allErrors.map((error, index) => (
              <PDFText key={index} style={styles.errorText}>
                • {error}
              </PDFText>
            ))}
          </View>
        )}

        {component ? (
          component
        ) : (
          <View>
            <PDFText style={styles.title}>Preview do Template</PDFText>
            <PDFText style={styles.text}>
              O HTML fornecido não pôde ser convertido para PDF. Verifique a estrutura do template.
            </PDFText>

            <PDFText style={styles.subtitle}>Dados Mock:</PDFText>
            <PDFText style={styles.text}>Empresa: {mockData.empresa.nomeFantasia}</PDFText>
            <PDFText style={styles.text}>Questionário: {mockData.questionario.nome}</PDFText>
            <PDFText style={styles.text}>Total de Grupos: {mockData.grupos.length}</PDFText>
            <PDFText style={styles.text}>Gerado em: {formatDate(mockData.dataGeracao)}</PDFText>
          </View>
        )}
      </Page>
    </Document>
  );
}

/**
 * TemplatePreview Component
 * @param root0
 * @param root0.htmlTemplate
 * @param root0.questionarioId
 * @param root0.templateName
 * @param root0.showDownload
 * @param root0.templateImages
 */
export function TemplatePreview({
  htmlTemplate,
  questionarioId,
  templateName = "template",
  showDownload = true,
  templateImages = []
}: TemplatePreviewProperties) {
  const [previewKey, setPreviewKey] = useState(0); // Force re-render

  // Load real grupos if questionarioId provided
  const { grupos, isLoading: isLoadingGrupos } = useQuestionarioGroups(questionarioId);

  // Load real results if available
  const { resultados, isLoading: isLoadingResultados, hasRealData } = usePreviewResultados(questionarioId);

  // Generate mock data (with real results if available)
  const mockData = useMemo(() => {
    if (isLoadingGrupos || isLoadingResultados) return null;
    // Pass real results if available, otherwise generateMockReportData will generate mock values
    return generateMockReportData(grupos, hasRealData ? resultados : undefined);
  }, [grupos, resultados, hasRealData, isLoadingGrupos, isLoadingResultados]);

  // Handle refresh preview
  const handleRefresh = useCallback(() => {
    setPreviewKey((previous) => previous + 1);
  }, []);

  // Generate filename with timestamp
  const filename = useMemo(() => {
    const timestamp = new Date().toISOString().slice(0, 10);
    return `${templateName.replaceAll(/\s+/g, "-").toLowerCase()}-preview-${timestamp}.pdf`;
  }, [templateName]);

  // Loading state
  if (isLoadingGrupos || isLoadingResultados || !mockData) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: 400 }}>
        <Loader size="lg" />
        <Text c="dimmed">Carregando dados para preview...</Text>
      </Stack>
    );
  }

  // Empty template state
  if (!htmlTemplate || htmlTemplate.trim().length === 0) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Template vazio" color="yellow" variant="light">
        Digite algum HTML no editor para visualizar o preview do PDF.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Actions */}
      <Group justify="space-between">
        <Group gap="xs">
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleRefresh} size="sm">
            Atualizar Preview
          </Button>

          {showDownload && (
            <Button
              component={PDFDownloadLink}
              document={<PDFDocument htmlContent={htmlTemplate} mockData={mockData} templateImages={templateImages} />}
              fileName={filename}
              variant="filled"
              color="blue"
              leftSection={<IconDownload size={16} />}
              size="sm"
              style={{ textDecoration: "none" }}
            >
              Baixar PDF de Teste
            </Button>
          )}
        </Group>

        <Group gap="xs">
          {hasRealData ? (
            <Badge color="green" variant="light" leftSection={<IconDatabase size={12} />}>
              Dados reais
            </Badge>
          ) : (
            <Badge color="yellow" variant="light" leftSection={<IconTestPipe size={12} />}>
              Dados mock
            </Badge>
          )}
        </Group>
      </Group>

      {/* PDF Viewer */}
      <Paper withBorder style={{ height: "70vh", overflow: "hidden" }}>
        <PDFViewer key={previewKey} width="100%" height="100%" showToolbar={false}>
          <PDFDocument htmlContent={htmlTemplate} mockData={mockData} templateImages={templateImages} />
        </PDFViewer>
      </Paper>

      {/* Info */}
      <Alert icon={<IconAlertCircle size={16} />} title="Sobre o Preview" color="blue" variant="light">
        <Stack gap="xs">
          {hasRealData ? (
            <Text size="sm">
              • O preview utiliza <strong>dados reais</strong> do diagnóstico ({resultados.length} scores carregados)
            </Text>
          ) : (
            <Text size="sm">
              • O preview utiliza <strong>dados mock</strong> (nenhum diagnóstico encontrado para esta empresa)
            </Text>
          )}
          <Text size="sm">• As variáveis são substituídas usando os mesmos processos do relatório real</Text>
          {questionarioId && grupos.length > 0 && (
            <Text size="sm">
              • Os grupos exibidos são <strong>reais</strong> do questionário selecionado ({grupos.length} grupos)
            </Text>
          )}
          <Text size="sm">
            • <strong>Componentes visuais</strong> ({`{{tabela_resultados}}, {{radar}}, etc.`}) não aparecem no preview
            PDF
          </Text>
          <Text size="sm">• Componentes visuais funcionam apenas em relatórios reais gerados pelo sistema</Text>
        </Stack>
      </Alert>
    </Stack>
  );
}
